import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomTable from "../../CommonComponents/Table/CustomTable";
import { networkService } from "../../config/axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { csrfToken } from "../../../utils/csrfTokenUtils";
import Paginate from "../../CommonComponents/Pagination/Paginate";
import UpdateRecipientModal from "../Stats/UpdateRecipientModal";
import DeleteOrgModal from "../../Modals/Admin/DeleteOrgModal";
import UserInfoContext from "../../../utils/userInfoContext";
import LoadingScreen from "../../Elements/LoadingScreen/LoadingScreen";

const RecipientsPage = () => {
    const navigate = useNavigate();
    const [recipients, setRecipients] = useState({});
    const [receiver, setReceiver] = useState({});
    const [page, setPage] = useState(1);
    const [editRecipientModal, setEditRecipientModal] = useState(null);
    const [recipientData, setRecipientData] = useState(null)
    const [loading, setLoading] = useState(false)
    const { setShowNotifications } = useContext(UserInfoContext)
    const isFetchingRef = useRef(false);
    const initialLoadRef = useRef(false);

    const fetchOrgsRecipients = useCallback(async (e) => {
        // Prevent duplicate calls
        if (isFetchingRef.current) {
            return;
        }
        
        setLoading(true)
        if (e) e.preventDefault();
        isFetchingRef.current = true;
        try {
            const response = await networkService("recipients/search", "POST", { page: 1, receiver, authenticity_token: csrfToken() });
            if (Object.keys(response?.data)?.length > 0) {
                setRecipients({ ...response?.data });
            } else {
                setRecipients([]);
            };
        } catch (error) {
            console.error("Error fetching recipients:", error);
        } finally {
            setLoading(false)
            isFetchingRef.current = false;
        }
    }, [receiver]);

    useEffect(() => {
        // Prevent duplicate initial load calls (React StrictMode)
        // Use local ref to handle StrictMode double-invocation
        // The global deduplication in networkService will handle simultaneous calls
        if (initialLoadRef.current || isFetchingRef.current) {
            return;
        }
        initialLoadRef.current = true;
        fetchOrgsRecipients();
    }, [fetchOrgsRecipients]);

    const resetFilters = (e) => {
        e.preventDefault();
        setReceiver({});
        fetchOrgsRecipients();
    }

    const handleFilters = (key, value) => {
        setReceiver({
            ...receiver,
            [key]: value,
        });
    }

    const handleUpdateReceipient = useCallback(async (formData) => {
        let payload = {};
        payload["receiver"] = {
            ...formData,
            name: `${formData?.first_name} ${formData?.last_name}`,
        };
        if (formData?.id == undefined) {
            payload["company_id"] = editRecipientModal?.tracking_link?.company_id;
            payload["session_id"] = editRecipientModal?.id;
            payload["campaign_id"] = editRecipientModal?.tracking_link?.campaign_id;
            payload["tracking_link_id"] = editRecipientModal?.tracking_link?.id;
        }
        const { data, status } = await networkService(
            `/receivers/${formData?.id}/${formData?.id == undefined ? "add_receiver_info" : "update_receiver_info"
            }`,
            "POST",
            payload
        );
        fetchOrgsRecipients();
        setEditRecipientModal(null);
    }, [editRecipientModal, fetchOrgsRecipients]);

    const handleDelete = async () => {
        try {
            const response = await networkService(`recipients/${recipientData?.id}`, 'DELETE');
            if (response.status == 200) {
                setRecipientData(null)
                setShowNotifications({
                    showToast: true,
                    toastType: "success",
                    toastMessage: "user has been deleted",
                })
                fetchOrgsRecipients();
            }
        } catch (error) {
            setShowNotifications({
                showToast: true,
                toastType: 'danger',
                toastMessage: "Something went wrong"
            })
        }
    }

    const columns = React.useMemo(() => {
        return [
            {
                Header: 'Email',
                accessor: 'email',
                Cell: ({ row }) => (
                    <a
                        className="cursor-pointer"
                        style={{ color: '#337ab7', textDecoration: 'none' }}
                        onClick={() => navigate(`/recipients/${row?.original?.id}`)}
                        title={`${row?.original?.first_name || ''} ${row?.original?.last_name || ''}`.trim()}
                    >
                        {row?.original?.email}
                    </a>
                ),
            },
            {
                Header: 'Name',
                accessor: 'full_name',
            },
            {
                Header: 'LLs',
                accessor: 'tracking_links',
                Cell: ({ row }) => (
                    <a
                        className="cursor-pointer"
                        style={{ color: '#337ab7', textDecoration: 'none' }}
                        onClick={() => navigate(`/links?receiver_id=${row?.original?.id}`)}
                        title="View LaserLINX for this recipient"
                    >
                        {row?.original?.tracking_links?.length || 0}
                    </a>
                )
            },
            {
                Header: 'Visits',
                accessor: 'visits',
                Cell: ({ row }) => (
                    <div>{row?.original?.sessions?.length}</div>
                )
            },
            {
                Header: 'Pages',
                accessor: 'pages',
                Cell: ({ row }) => (
                    <span>{row?.original?.session_pages?.length}</span>
                )
            },
            {
                Header: 'Top Viewed KDF',
                accessor: 'top_visited_kdfs',
            },
            {
                Header: 'Most Visited KDF',
                accessor: 'most_visited_kdf',
                Cell: ({ row }) => (
                    <span>{row?.original?.most_visited_kdf?.title}</span>
                )
            },
            {
                Header: 'Actions',
                accessor: 'action',
                Cell: ({ row }) => (
                    <>
                    <img src="/icons/edit-blue.svg" className="cursor-pointer" onClick={() => setEditRecipientModal({ id: row?.original?.id, receiver: row?.original })} />
                    <img src="/icons/danger-delete.svg" className="cursor-pointer" onClick={() => {
                                setRecipientData({
                                    id: row?.original?.id,
                                    fullName: row?.original?.full_name
                                })
                            }} />
                    </>
                )
            },
        ];
    }, [recipients?.recipients]);

    return (
        <>
            <DeleteOrgModal
                title={"Delete User"}
                openModal={recipientData !== null}
                closeModal={() => setRecipientData(null)}
                name={'delete' + recipientData?.fullName}
                handleDelete={handleDelete}
            />
            <UpdateRecipientModal
                editRecipientModal={editRecipientModal}
                setEditRecipientModal={setEditRecipientModal}
                submitRecipientForm={handleUpdateReceipient}
            />
            {loading && <LoadingScreen />}
            <div className="header-content-main">
                <div className="doc-filter">
                    <h4 className="filter-recipients mb-2">Filters</h4>
                    <form className="d-flex recipients-form">
                        <div className="d-flex form-group">
                            <input className="kdf-Tags1 ms-0" onChange={({ target: { value } }) => handleFilters('email', value)} value={receiver?.email} placeholder="Email or reference no." />
                        </div>
                        <div className="d-flex form-group">
                            <input className="kdf-Tags1" type="text" onChange={({ target: { value } }) => handleFilters('name', value)} value={receiver?.name} placeholder="Name" />
                        </div>
                        <div className="d-flex form-group">
                            <input className="kdf-Tags1" type="text" onChange={({ target: { value } }) => handleFilters('title', value)} value={receiver?.title} placeholder="KDF Title" />
                        </div>

                        <div className="d-flex recepient-btn-grp">
                        <button className="btn btn-info  reset" onClick={fetchOrgsRecipients}>
                            Apply Filters
                        </button>
                        <button className="reset-btn" onClick={resetFilters}>
                         <img src="/icons/autorenew.svg"/>   Reset Filters
                        </button>
                        </div>
                    </form>
                </div>
            </div>
            {!loading && (
                <div className="recipient-table" style={{overflow: 'auto', margin: '15px auto', maxWidth: '960px'}}>
                    <CustomTable data={recipients?.recipients || []} columnData={columns} showFilters={false} />
                    {recipients?.total_recepient > 10 &&
                        <Paginate
                            currentPage={page}
                            totalPages={Math.ceil(recipients?.total_recepient / 10)}
                            onPageChange={(page = 1) => setPage(page)}
                            totalData={recipients?.total_recepient}
                            itemTitle={recipients?.total_recepient > 1 ? "Recipients" : "Recipient"}
                            pageSize={10}
                            currentCount={recipients?.recipients?.length}
                        />
                    }
                </div>
            )}
        </>
    )
}
export default RecipientsPage;
